import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Plus } from 'lucide-react';
import { PERMISSIONS, StocktakeStatus, type StocktakeSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useStocktakesQuery } from '../api/stocktake.api';
import { OpenStocktakeDrawer } from '../components/OpenStocktakeDrawer';
import { StocktakeStatusBadge } from '../components/StocktakeStatusBadge';

const PAGE_SIZE = 20;

/** Physical counts. Opening one leads straight to its sheet, which is where the work happens. */
export function StocktakePage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { activeBranch } = useBranch();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StocktakeStatus | ''>('');
  const [page, setPage] = useState(1);
  const [isOpening, setIsOpening] = useState(false);

  const deferredSearch = useDeferredValue(search);

  const { data, isPending, isError, error, refetch, isFetching } = useStocktakesQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    status,
  });

  const canCount = can(PERMISSIONS.INVENTORY_ADJUST);

  const columns: Column<StocktakeSummary>[] = [
    {
      key: 'reference',
      header: t('inventory.stocktake.fields.reference'),
      primary: true,
      render: (session) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {session.reference}
          </span>
          <span className="text-muted text-xs">
            {session.name ? `${session.name} · ` : ''}
            {session.branchName}
          </span>
        </span>
      ),
    },
    {
      key: 'progress',
      header: t('inventory.stocktake.fields.progress'),
      align: 'end',
      render: (session) => (
        <span className="text-ink" dir="ltr">
          {session.countedLineCount} / {session.lineCount}
        </span>
      ),
    },
    {
      key: 'variance',
      header: t('inventory.stocktake.fields.variance'),
      align: 'end',
      render: (session) => (
        <span
          className={
            session.varianceLineCount > 0 ? 'text-sm font-semibold text-amber-700' : 'text-muted'
          }
          dir="ltr"
        >
          {session.varianceLineCount > 0
            ? `${session.varianceLineCount} · ${session.netVariance > 0 ? '+' : ''}${session.netVariance}`
            : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('inventory.fields.state'),
      render: (session) => <StocktakeStatusBadge status={session.status} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (session) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/inventory/stocktake/${session.id}`)}
          aria-label={t('inventory.stocktake.openNamed', { reference: session.reference })}
        >
          {t('inventory.stocktake.openSheet')}
        </Button>
      ),
    },
  ];

  const statusOptions = [
    { value: '', label: t('inventory.stocktake.filters.all') },
    ...Object.values(StocktakeStatus).map((value) => ({
      value,
      label: t(`inventory.stocktake.status.${value}`),
    })),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('inventory.stocktake.title')}
        subtitle={t('inventory.stocktake.subtitle')}
        actions={
          canCount && activeBranch ? (
            <Button onClick={() => setIsOpening(true)}>
              <Plus className="size-4" aria-hidden />
              {t('inventory.stocktake.open')}
            </Button>
          ) : undefined
        }
      />

      {!activeBranch ? <Alert tone="warning">{t('inventory.stocktake.selectBranch')}</Alert> : null}

      <Card>
        <CardHeader
          title={t('inventory.stocktake.title')}
          subtitle={
            data ? t('inventory.stocktake.count', { count: data.pagination.totalItems }) : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-52">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t('inventory.stocktake.searchPlaceholder')}
                  aria-label={t('inventory.stocktake.searchLabel')}
                />
              </div>
              <div className="w-36">
                <Select
                  options={statusOptions}
                  value={status}
                  aria-label={t('inventory.fields.state')}
                  onChange={(event) => {
                    setStatus(event.target.value as StocktakeStatus | '');
                    setPage(1);
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('inventory.stocktake.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(session) => session.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('inventory.stocktake.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={ClipboardList}
          emptyTitle={
            deferredSearch || status
              ? t('inventory.stocktake.noMatches')
              : t('inventory.stocktake.empty')
          }
          emptyDescription={
            deferredSearch || status
              ? t('inventory.stocktake.noMatchesHint')
              : t('inventory.stocktake.emptyHint')
          }
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>

      <OpenStocktakeDrawer
        isOpen={isOpening}
        onClose={() => setIsOpening(false)}
        onOpened={(session) => navigate(`/inventory/stocktake/${session.id}`)}
      />
    </div>
  );
}
